import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { styled } from "styled-components";
import { t, MessageKey } from "translations";

import {
  Button,
  CTALoader,
  ErrorBanner,
  Form,
  Loader,
  RadioGroup,
  RadioGroupOption,
  SubmitButton,
} from "ui";
import { SmallText } from "ui/styles/text";
import { media } from "ui/themes/signup";

import { useCreatePaymentSignature } from "../../hooks/useCreatePaymentSignature";
import { FlowType, HostedPageParams, PrepopulateFields } from "../../types";
import { throwError } from "../../utils/errorHandler";
import { logger } from "../../utils/logger";
import { submitForm, addEventHandler } from "../../utils/zuora";
import { getZuoraErrorMessageKey } from "../../utils/zuoraErrorHandler";
import { PaymentDisclaimer } from "../PaymentDisclaimer";

import CreditCardLogo from "./assets/credit.svg?react";
import PaypalLogo from "./assets/paypal.svg?react";
import { CreditCardForm } from "./CreditCardForm";
import { PayPalForm, PayPalFormProps } from "./PayPalForm";

const PAGE_URL = import.meta.env.VITE_ZUORA_PAGE_URL;

type PaymentMethod = "card" | "paypal";

const PaymentForm = styled(Form)`
  align-items: center;
  gap: 25px;

  & > div {
    width: 100%;
  }

  /* Overrides default form behaviour */
  ${Button}[type="submit"] {
    align-self: center;
    margin: 0;
  }

  ${media.xlarge`
    align-items: flex-start;
  `}
`;

const MethodContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const LabelText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface PaymentMethodFormProps {
  creditCard: {
    pageId: string;
    gatewayName: string;
    prepopulatedFields: PrepopulateFields;
    onSubmit: React.MouseEventHandler<HTMLButtonElement>;
  };
  paypal: {
    gatewayName: string;
    componentId: PayPalFormProps["id"];
    merchantId: string;
    onSubmit: PayPalFormProps["onStart"];
  };
  flowType: FlowType;
}

const parseZuoraError = (zuoraResponse: string): MessageKey => {
  try {
    const zuoraData = JSON.parse(zuoraResponse);
    const errorMessageKey = getZuoraErrorMessageKey(
      zuoraData.code,
      zuoraData.message,
    );
    logger.warn("Zuora error", { zuoraData, errorMessageKey });
    return errorMessageKey;
  } catch (error) {
    logger.error("Failed parsing zuoraResponse for error", {
      error,
      zuoraResponse,
    });
    return "checkout_payment_generic_error";
  }
};

const useGetError = () => {
  const [errorMessage, setErrorMessage] = useState<MessageKey | null>(null);
  // These are not reactive (setter on router), so this functions.
  // If we were to modify these on this page, we would probably have to add an storage listener.
  const zuoraResponse = window.sessionStorage.getItem("zuoraResponse");
  const paypalError = window.sessionStorage.getItem("paypalError");

  const clearError = () => {
    window.sessionStorage.removeItem("zuoraResponse");
    window.sessionStorage.removeItem("paypalError");
    setErrorMessage(null);
    return;
  };
  useEffect(() => {
    let messageKey: MessageKey | null = null;
    if (paypalError) {
      messageKey = "checkout_payment_generic_error";
    }
    if (zuoraResponse) {
      messageKey = parseZuoraError(zuoraResponse);
    }
    setErrorMessage(messageKey);
  }, [zuoraResponse, paypalError]);

  return { errorMessage, clearError };
};

export const PaymentMethodForm = ({
  flowType,
  creditCard,
  paypal,
}: PaymentMethodFormProps) => {
  const intl = useIntl();

  const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
  const [isZuoraLoaded, setIsZuoraLoaded] = useState<boolean>(false);
  const { errorMessage, clearError } = useGetError();

  const { register, watch } = useForm<{
    paymentMethod: PaymentMethod;
  }>({
    defaultValues: {
      paymentMethod: undefined,
    },
  });
  const paymentMethod = watch("paymentMethod");

  // Use Zuora event handler for any side-effects
  const onZuoraSubmit = () => {
    clearError();
    setDisableSubmit(true);
  };
  addEventHandler("onSubmit", onZuoraSubmit);

  const onZuoraLoad = () => {
    setDisableSubmit(false);
    setIsZuoraLoaded(true);
  };
  addEventHandler("onloadCallback", onZuoraLoad);

  const onZuoraUnload = () => {
    setDisableSubmit(true);
    setIsZuoraLoaded(false);
  };

  const {
    data: paymentSignature,
    isPending,
    error,
    mutate: getSignature,
  } = useCreatePaymentSignature();

  const pageId = creditCard.pageId; // can be an empty string until gateways are fetched
  // On payment method selection change
  useEffect(() => {
    if (paymentMethod !== "card") {
      onZuoraUnload();
    }

    if (paymentMethod === "card" && pageId) {
      getSignature({ pageId });
    }
  }, [pageId, getSignature, paymentMethod]);

  if (error) {
    throwError("failedPaymentSignatureCreation", error);
  }

  const hostedPageParams: HostedPageParams | undefined = paymentSignature && {
    ...paymentSignature,
    id: creditCard.pageId,
    paymentGateway: creditCard.gatewayName,
    style: "inline",
    submitEnabled: false,
    locale: intl.locale,
    url: PAGE_URL,
    field_passthrough1: flowType,
  };

  const isCCFormReadyForRendering = !isPending && hostedPageParams;
  const showLoader = !isZuoraLoaded;

  const creditCardForm = paymentMethod === "card" && (
    <MethodContainer>
      {showLoader && <Loader isWholePage={false} />}
      {isCCFormReadyForRendering && (
        <>
          <CreditCardForm
            hostedPageParams={hostedPageParams}
            prepopulatedFields={creditCard.prepopulatedFields}
            errorCallback={(args) => {
              logger.warn("CreditCardForm client error", { ...args });
            }}
          />
          <SubmitButton
            disabled={!isZuoraLoaded || disableSubmit}
            onClick={(e) => {
              e.preventDefault();
              creditCard.onSubmit(e);
              submitForm();
            }}
            data-testid="credit-card-submit-button"
          >
            {disableSubmit ? (
              <CTALoader />
            ) : (
              t("membership_signup_payment_method_button_submit")
            )}
          </SubmitButton>
        </>
      )}
    </MethodContainer>
  );

  const paypalForm = paymentMethod === "paypal" && (
    <MethodContainer>
      <PayPalForm
        id={paypal.componentId}
        onStart={paypal.onSubmit}
        paypalMerchantId={paypal.merchantId}
      />
    </MethodContainer>
  );

  return (
    <PaymentForm
      onSubmit={(e) => {
        e.preventDefault();
      }}
    >
      {errorMessage && (
        <ErrorBanner marginBottom="15px">{t(errorMessage)}</ErrorBanner>
      )}

      <RadioGroup>
        <RadioGroupOption
          {...register("paymentMethod")}
          data-testid="credit-card-selector-button"
          value="card"
          labelText={
            <LabelText>
              <CreditCardLogo height="24px" />
              <SmallText $fontWeight="bold" $color="white">
                {t("membership_hub_credit_card")}
              </SmallText>
            </LabelText>
          }
        >
          {creditCardForm}
        </RadioGroupOption>
        <RadioGroupOption
          {...register("paymentMethod")}
          data-testid="paypal-selector-button"
          value="paypal"
          labelText={
            <LabelText>
              <PaypalLogo height="24px" />
              <SmallText $fontWeight="bold" $color="white">
                {t("membership_hub_paypal")}
              </SmallText>
            </LabelText>
          }
        >
          {paypalForm}
        </RadioGroupOption>
      </RadioGroup>
      <PaymentDisclaimer />
    </PaymentForm>
  );
};
